import { useState, useEffect, useRef, useCallback } from 'react';

// ── Types ────────────────────────────────────────────────────────────────────

type Phase = 'waiting' | 'active' | 'done';

interface GuessResult {
  word: string;
  score: number;
  temperature: string;
}

interface SavedState {
  targetWord: string;
  guesses: GuessResult[];
}

interface WordAssocData {
  target_word: string;
  hint_category: string;
  difficulty: string;
}

interface Props {
  nanoid: string;
  wordAssoc: WordAssocData;
}

const WAIT_SECONDS = 10;
const GAME_SECONDS = 120;

const DIFF_STYLE: Record<string, { color: string; bg: string }> = {
  easy:   { color: '#2d6e4e', bg: '#e8f5ee' },
  medium: { color: '#7a5200', bg: '#fff3d6' },
  hard:   { color: '#a02020', bg: '#fdecea' },
};

function scoreColor(score: number): string {
  if (score >= 50) return '#c0392b';
  if (score >= 35) return '#d35400';
  if (score >= 20) return '#b7950b';
  if (score >= 8)  return '#2471a3';
  return '#999';
}

function formatTime(s: number): string {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function WordAssocPuzzle({ nanoid, wordAssoc }: Props) {
  const { target_word, hint_category, difficulty } = wordAssoc ?? {};
  const lsKey = `pz_wordassoc_${nanoid}`;
  const diffStyle = DIFF_STYLE[difficulty] ?? DIFF_STYLE.medium;

  // Restore completed game from localStorage
  const [phase, setPhase] = useState<Phase>(() => {
    try {
      const raw = localStorage.getItem(lsKey);
      if (raw) {
        const s: SavedState = JSON.parse(raw);
        if (s.targetWord === target_word) return 'done';
      }
    } catch {}
    return 'waiting';
  });

  const [guesses, setGuesses] = useState<GuessResult[]>(() => {
    try {
      const raw = localStorage.getItem(lsKey);
      if (raw) {
        const s: SavedState = JSON.parse(raw);
        if (s.targetWord === target_word) return s.guesses ?? [];
      }
    } catch {}
    return [];
  });

  const [countdown, setCountdown] = useState(WAIT_SECONDS);
  const [timeLeft, setTimeLeft] = useState(GAME_SECONDS);
  const [input, setInput] = useState('');
  const [rejectionMsg, setRejectionMsg] = useState('');
  const [answerKey, setAnswerKey] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingRef = useRef<Set<string>>(new Set());
  const rejectionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Timers ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (phase === 'done') return;
    const tick = setInterval(() => {
      if (phase === 'waiting') setCountdown(p => Math.max(0, p - 1));
      else if (phase === 'active') setTimeLeft(p => Math.max(0, p - 1));
    }, 1000);
    return () => clearInterval(tick);
  }, [phase]);

  // Transition: waiting → active
  useEffect(() => {
    if (phase === 'waiting' && countdown === 0) setPhase('active');
  }, [countdown, phase]);

  // Transition: active → done
  useEffect(() => {
    if (phase === 'active' && timeLeft === 0) setPhase('done');
  }, [timeLeft, phase]);

  // Focus input when game goes active
  useEffect(() => {
    if (phase === 'active') inputRef.current?.focus();
  }, [phase]);

  // Persist completed game
  useEffect(() => {
    if (phase === 'done') {
      try {
        localStorage.setItem(lsKey, JSON.stringify({ targetWord: target_word, guesses }));
      } catch {}
    }
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch answer key when game ends
  useEffect(() => {
    if (phase !== 'done') return;
    fetch(`/api/puzzles/${nanoid}/wordassoc/answerkey`)
      .then(r => r.json())
      .then(data => {
        if (data.success && Array.isArray(data.data?.top_answers)) {
          setAnswerKey(data.data.top_answers);
        }
      })
      .catch(() => {});
  }, [phase, nanoid]);

  // Clear rejection message timer on unmount
  useEffect(() => {
    return () => {
      if (rejectionTimerRef.current) clearTimeout(rejectionTimerRef.current);
    };
  }, []);

  // ── Guess submission ────────────────────────────────────────────────────────

  const showRejection = (msg: string) => {
    setRejectionMsg(msg);
    if (rejectionTimerRef.current) clearTimeout(rejectionTimerRef.current);
    rejectionTimerRef.current = setTimeout(() => setRejectionMsg(''), 3000);
  };

  const submit = useCallback(async () => {
    if (phase !== 'active') return;
    const word = input.trim().toLowerCase().replace(/[^a-z-]/g, '');
    if (!word) return;
    setInput('');

    if (word === target_word.toLowerCase()) {
      showRejection(`"${word}" is the target word — try associated words instead!`);
      return;
    }

    const alreadyDone = guesses.some(g => g.word === word) || pendingRef.current.has(word);
    if (alreadyDone) return;

    pendingRef.current.add(word);
    try {
      const res = await fetch(`/api/puzzles/${nanoid}/wordassoc/guess`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ word }),
      });
      const data = await res.json();
      if (data.success) {
        setGuesses(prev => [...prev, { word, score: data.data.score, temperature: data.data.temperature }]);
      } else if (data.error === 'WORD_IS_TARGET') {
        showRejection(`"${word}" is the target word — try associated words instead!`);
      }
    } catch {
      // silent — don't break flow during timed game
    } finally {
      pendingRef.current.delete(word);
    }
  }, [phase, input, guesses, nanoid, target_word]);

  // ── Derived ─────────────────────────────────────────────────────────────────

  const totalScore = guesses.reduce((sum, g) => sum + g.score, 0);
  const sortedGuesses = [...guesses].sort((a, b) => b.score - a.score);
  const isLowTime = timeLeft <= 30 && phase === 'active';

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="pz-wordassoc">

      {/* Target word — always visible */}
      <div className="pz-wa-target-block">
        <p className="pz-wa-target-label">Today's word</p>
        <p className="pz-wa-target-word">{target_word?.toUpperCase()}</p>
        <div className="pz-wa-meta">
          <span className="pz-wa-category">Category: <strong>{hint_category}</strong></span>
          <span className="pz-difficulty-badge" style={{ color: diffStyle.color, background: diffStyle.bg }}>
            {difficulty?.charAt(0).toUpperCase()}{difficulty?.slice(1)}
          </span>
        </div>
      </div>

      {/* Waiting phase */}
      {phase === 'waiting' && (
        <div className="pz-wa-waiting">
          <p className="pz-wa-instructions">
            Think of as many words associated with <strong>{target_word}</strong> as you can.
            You have 2 minutes — type fast!
          </p>
          <div className="pz-wa-countdown-wrap">
            <span className="pz-wa-countdown-num">{countdown}</span>
            <span className="pz-wa-countdown-label">seconds until start</span>
          </div>
        </div>
      )}

      {/* Active phase */}
      {phase === 'active' && (
        <>
          <div className="pz-wa-hud">
            <span className={`pz-wa-timer ${isLowTime ? 'pz-wa-timer-low' : ''}`}>
              {formatTime(timeLeft)}
            </span>
            <span className="pz-wa-score-live">
              {totalScore} pt{totalScore !== 1 ? 's' : ''}
            </span>
            <span className="pz-wa-guess-count">
              {guesses.length} word{guesses.length !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="pz-wa-input-row">
            <input
              ref={inputRef}
              type="text"
              className="pz-wa-input"
              placeholder="Type a word and press Enter..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
            />
            <button
              className="pz-btn-primary pz-btn-sm"
              onClick={submit}
              disabled={!input.trim()}
            >
              Submit
            </button>
          </div>

          {rejectionMsg && (
            <p className="pz-wa-rejection">{rejectionMsg}</p>
          )}
        </>
      )}

      {/* Done phase */}
      {phase === 'done' && (
        <div className="pz-wa-results">
          <div className="pz-wa-results-score">
            <span className="pz-wa-final-pts">{totalScore}</span>
            <span className="pz-wa-final-label">points</span>
          </div>
          <p className="pz-wa-final-sub">{guesses.length} word{guesses.length !== 1 ? 's' : ''} submitted</p>

          {answerKey.length > 0 && (
            <div className="pz-wa-answer-key">
              <p className="pz-wa-answer-key-label">Top answers for "{target_word}"</p>
              <div className="pz-wa-answer-key-list">
                {answerKey.map((w, i) => {
                  const myGuess = guesses.find(g => g.word === w);
                  return (
                    <span
                      key={i}
                      className={`pz-wa-answer-key-word ${myGuess ? 'pz-wa-answer-key-hit' : ''}`}
                      title={myGuess ? `You got this! (${myGuess.score} pts)` : undefined}
                    >
                      {w}{myGuess ? ' ✓' : ''}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Guess list — shown during active and done */}
      {sortedGuesses.length > 0 && (
        <div className="pz-wa-guesses">
          {sortedGuesses.map((g, i) => (
            <div key={i} className="pz-wa-guess-row">
              <span className="pz-wa-word">{g.word}</span>
              <div className="pz-wa-bar-track">
                <div
                  className="pz-wa-bar-fill"
                  style={{
                    width: `${Math.min(100, Math.round((g.score / 70) * 100))}%`,
                    background: scoreColor(g.score),
                  }}
                />
              </div>
              <span className="pz-wa-score" style={{ color: scoreColor(g.score) }}>{g.score}</span>
              <span className="pz-wa-temp" style={{ color: scoreColor(g.score) }}>{g.temperature}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
